/**
 * 경원(아스콘) 출하 가상 자료 — OKIS 동기화(경원 준비 §6 ④) 전 화면 확인용. Firestore 와 무관.
 *
 * - 이름은 전부 "OO·△△" 같은 가짜, 차량번호도 가짜(실데이터 꼴 "80-1001-0" 등). 실제 OKIS 자료를 옮겨 오지 않는다.
 * - 날짜를 오늘 기준 상대값으로 만든다(날짜가 지나도 오늘·다가오는 일정이 늘 보이게).
 *     오늘      : 3건(관급 1·사급 2), 1건 출하 중 · 1건 완료 · 1건 대기 — 합계 수주 320톤
 *     +3일·+9일 : 예정만(계근 없음) — 내 업무 홈 다가오는 일정과 같은 값
 *     지난 날   : 날짜로 정해지는 의사난수 — 일요일 쉼, 나머지 날 약 55% 생산, 전부 마감
 * - 출하누계 = Σ 계근 실중량 ÷ 1000 (OKIS 와 같은 관계)으로 계산해 수주·계근이 서로 맞는다.
 * - 실데이터에 맞춤(10-22 수집기): 규격 KIND 는 괄호 꼴 "표층용(WC-2)", 첫차시간은 거의 비어 있음(오늘 관급 1건만 입력),
 *   계근 1대 약 26톤(24~28톤).
 */
import type { AsconOrder, AsconTicket } from "@/domain/asconShipment";

interface OrderSeed {
  custCode: string;
  company: string;
  site: string;
  kind: string;
  maxAgg: string;
  orderT: number;
  firstTime: string;
  distKm: number;
  /** 오늘 시드에서 이미 계근된 차수 (undefined = 전량 출하·마감) */
  loadsDone?: number;
  /** 수주 메모 MEMO (가짜) */
  memo?: string;
  /** 위치 GROUND (가짜, 읍·면·마을 수준 — 실데이터 꼴) */
  location?: string;
  /** 첫차시간을 OKIS 에 입력했는지 — 실데이터는 거의 안 함(00:00). 시드 firstTime 은 계근 시각 만들기에만 쓴다 */
  timeEntered?: boolean;
}

const GOV = { custCode: "A002", company: "OO아스콘사업협동조합" };
const GOV2 = { custCode: "A034", company: "OO도로개량사업단" };
const CUSTS = [
  { custCode: "B011", company: "△△산업" },
  { custCode: "B027", company: "□□개발" },
  { custCode: "B034", company: "◇◇건설" },
  { custCode: "A033", company: "OO축산업협동조합" }, // 이름에 "조합"이 있어도 사급(코드 판정 확인용)
];
const SITES = ["OO지구 도로포장", "△△로 보수", "□□단지 진입로", "◇◇리 농로 포장", "OO교차로 개선", "△△공단 내부도로", "□□마을 안길"];
// 실데이터 꼴(R_SUJU KIND | V_RULE): 등급 있는 것·없는 것, V_RULE "13"·"13mm" 섞임
const KINDS: Array<[string, string]> = [
  ["표층용(WC-2)", "13"], ["기층용(BB-2)", "30"], ["표층용(WC-3)1등급", "20mm"], ["기층용(BB-2)2등급", "30mm"],
  ["표층용(WC-6) 1등급", "13"], ["표층용 (WC-4)", "20"], ["중간층용(MC-1)1등급", "20mm"],
];
// 실데이터 꼴(00-0000-0 대부분, 000가0000 등) — 번호는 가짜
const CARS = ["80-1001-0", "80-1002-0", "80-1003-0", "81-2004-0", "123가1005", "12-1006"];

/** 오늘 기준 상대 날짜 시드 */
const FIXED: Record<number, OrderSeed[]> = {
  0: [
    { ...GOV, site: "OO지구 도로포장", kind: "표층용(WC-2)", maxAgg: "13", orderT: 180, firstTime: "07:30", distKm: 12, loadsDone: 4, memo: "오전 포장 · 덤프 대기 위치 현장 입구", location: "OO읍 △△리 일원", timeEntered: true },
    { ...CUSTS[0], site: "△△로 보수", kind: "기층용(BB-2)2등급", maxAgg: "30mm", orderT: 90, firstTime: "08:00", distKm: 7, location: "□□면 ◇◇마을 앞", memo: "현장 담당 / 010-0000-0000" },
    { ...CUSTS[1], site: "□□단지 진입로", kind: "표층용(WC-2)", maxAgg: "13", orderT: 50, firstTime: "13:00", distKm: 18, loadsDone: 0 },
  ],
  3: [
    { ...GOV, site: "△△로 확장공사", kind: "표층용(WC-2)", maxAgg: "13", orderT: 420, firstTime: "07:00", distKm: 21, loadsDone: 0 },
    { ...GOV2, site: "OO교차로 개선", kind: "기층용(BB-2)", maxAgg: "20", orderT: 300, firstTime: "07:30", distKm: 15, loadsDone: 0 },
    { ...CUSTS[2], site: "◇◇리 농로 포장", kind: "표층용(WC-1)", maxAgg: "13", orderT: 140, firstTime: "09:00", distKm: 9, loadsDone: 0 },
  ],
  9: [
    { ...GOV, site: "□□산단 진입로", kind: "표층용(WC-2)", maxAgg: "13", orderT: 380, firstTime: "07:00", distKm: 25, loadsDone: 0 },
    { ...CUSTS[3], site: "□□마을 안길", kind: "표층용(WC-1)", maxAgg: "13", orderT: 160, firstTime: "08:30", distKm: 6, loadsDone: 0 },
  ],
};

// ── 날짜 → 의사난수 (같은 날짜면 늘 같은 값) ──
function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function rng(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86400000);
}
function addMin(hhmm: string, min: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const t = Math.min(h * 60 + m + min, 23 * 60 + 59);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}

function pastSeeds(date: string): OrderSeed[] {
  const r = rng(hashStr(date));
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
  if (dow === 0 || r() > 0.55) return [];
  const n = 1 + Math.floor(r() * 4);
  const out: OrderSeed[] = [];
  for (let i = 0; i < n; i += 1) {
    const gov = r() < 0.4;
    const who = gov ? (r() < 0.85 ? GOV : GOV2) : CUSTS[Math.floor(r() * CUSTS.length)];
    const [kind, maxAgg] = KINDS[Math.floor(r() * KINDS.length)];
    const orderT = (gov ? 120 : 30) + Math.round(r() * (gov ? 30 : 12)) * 10;
    out.push({
      ...who,
      site: SITES[Math.floor(r() * SITES.length)],
      kind, maxAgg,
      orderT,
      firstTime: addMin("07:00", Math.floor(r() * 8) * 30),
      distKm: 4 + Math.floor(r() * 22),
      // 6건 중 1건꼴: 날씨 등으로 덜 나가고 끝남(출하마감) — 수주량의 60% 안쪽(1대 최대 28톤), 작은 수주는 0대(전부 다른 날로)
      ...(r() < 0.17 ? { loadsDone: Math.floor((orderT * 0.6) / 28) } : {}),
    });
  }
  return out;
}

/** 시드 → 수주·계근. 계근 1대 24~28톤, 출하누계는 계근 합으로 */
function build(date: string, seeds: OrderSeed[]): { orders: AsconOrder[]; tickets: AsconTicket[] } {
  const r = rng(hashStr(`${date}#t`));
  const orders: AsconOrder[] = [];
  const tickets: AsconTicket[] = [];
  let carCursor = 0;
  seeds.forEach((sd, i) => {
    const seq = String(i + 1).padStart(2, "0");
    let leftKg = sd.orderT * 1000;
    let netSum = 0;
    // 전량 출하(loadsDone 없음)면 남은 양이 한 차(28톤) 이하가 될 때까지 싣고, 마지막 차는 남은 양보다 조금 덜(0~0.3톤).
    // 일부 출하(오늘)면 정한 차수만큼만.
    for (let k = 0; sd.loadsDone === undefined ? leftKg > 0 : k < sd.loadsDone; k += 1) {
      const last = sd.loadsDone === undefined && leftKg <= 28000;
      const load = 24000 + Math.round(r() * 40) * 100;
      // 전량 출하 중간 차는 마지막 차 몫(4톤 이상)을 남긴다
      const net = last
        ? Math.max(1000, leftKg - Math.round(r() * 3) * 100)
        : sd.loadsDone === undefined ? Math.min(load, leftKg - 4000) : load;
      leftKg -= net;
      netSum += net;
      const tare = 14000 + Math.round(r() * 30) * 100;
      // 30건 중 1건꼴 수기 보정(총중량 − 공차 ≠ 실중량)
      const gross = tare + net + (r() < 0.033 ? 200 : 0);
      tickets.push({
        id: `${date}_${seq}_${k + 1}`,
        date, time: addMin(sd.firstTime, k * 14 + Math.floor(r() * 5)), orderSeq: seq, no: k + 1,
        carNo: CARS[(carCursor + k) % CARS.length],
        grossKg: gross, tareKg: tare, netKg: net,
        tempC: 160 + Math.round(r() * 15),
        operator: "계근 담당",
      });
      if (last) break;
    }
    carCursor += 2;
    const shippedT = Math.round(netSum) / 1000;
    const remainT = Math.round((sd.orderT - shippedT) * 1000) / 1000;
    orders.push({
      id: `${date}_${seq}`, date, seq,
      custCode: sd.custCode, siteCode: String(100 + i).padStart(3, "0"),
      company: sd.company, site: sd.site,
      sizeCode: sd.kind.includes("WC") ? "1013" : "2020",
      kind: sd.kind, maxAgg: sd.maxAgg,
      orderT: sd.orderT, shippedT, remainT,
      // 실데이터는 V_STATE 를 사실상 안 씀(대기중 719·마감 1) — 가상 자료도 전부 대기중
      state: "대기중",
      firstTime: sd.timeEntered ? sd.firstTime : "", distKm: sd.distKm, memo: sd.memo ?? "", location: sd.location ?? "",
    });
  });
  return { orders, tickets };
}

/** 그날의 가상 수주·계근. today 이후 날짜는 고정 시드(+3·+9일)만, 지난 날은 의사난수 */
export function mockAsconDay(date: string, today: string): { orders: AsconOrder[]; tickets: AsconTicket[] } {
  const off = dayDiff(date, today);
  if (off >= 0) return build(date, FIXED[off] ?? []);
  return build(date, pastSeeds(date));
}
